import { Gender } from '../testConfiguration';

// ---- Typy i stałe ----
type LeadName = 'V1' | 'I' | 'V2' | 'V4' | 'V6' | 'II';
type Territory = 'anterior' | 'lateral' | 'inferior';
type Summary = 'ok' | 'warn' | 'alert';

const LEADS: LeadName[] = ['V1', 'I', 'V2', 'V4', 'V6', 'II'];
const TERRITORIES: Record<Territory, LeadName[]> = {
  anterior: ['V1', 'V2', 'V4'],
  lateral: ['I', 'V6'],
  inferior: ['II'], // jedno odprowadzenie → nie spełni wymogu "≥2"
};
const TERR_PL: Record<Territory, string> = {
  anterior: 'anterior',
  lateral: 'lateral',
  inferior: 'inferior',
};

// ---- Teksty katalogowe (krótkie, gotowe do użycia) ----
const TEXTS = {
  norm: 'st_tips_norm',
  stemi: (where: string) => `st_tips_stemi_${where}`,
  depression: (where: string) => `st_tips_depr_${where}`,
  mild: 'st_tips_mild',
};

// ---- Wyniki pośrednie (techniczne) ----
interface PerLeadResult {
  uv: number; // wejście w µV
  mv: number; // mV (3 miejsca dla obliczeń)
  inNorm: boolean; // |ST| ≤ 0.10 mV (prosta norma spoczynkowa)
  elevation: boolean; // uniesienie ≥ próg STEMI
  depression: boolean; // obniżenie ≤ -0.05 mV
}

interface Interpretation {
  perLead: Record<LeadName, PerLeadResult>;
  overallInNorm: boolean;
  stemiTerritories: Territory[]; // ≥2 uniesienia w terytorium
  depressionTerritories: Territory[]; // ≥2 depresje w terytorium
}

// ---- Progi ----
function elevationThreshold(lead: LeadName, gender: Gender): number {
  // Dorośli: V2 (i V3 gdyby było) — mężczyzna 0.20 mV, kobieta 0.15 mV; pozostałe 0.10 mV
  if (lead === 'V2') {
    return gender === Gender.Male ? 0.2 : 0.15;
  }
  return 0.1;
}
function depressionThreshold(): number {
  return 0.05;
}

// ---- Rdzeń analizy (jak poprzednio, ale zredukowany do potrzeb rekordu) ----
function interpretST(rawUv: number[], gender: Gender): Interpretation {
  if (rawUv.length !== LEADS.length) {
    throw new Error(
      'Oczekiwano 6 wartości w kolejności [V1, I, V2, V4, V6, II].',
    );
  }
  const perLead = {} as Record<LeadName, PerLeadResult>;
  let overallInNorm = true;

  LEADS.forEach((lead, i) => {
    const uv = rawUv[i];
    const mv = uv / 1000; // µV → mV
    const elevThr = elevationThreshold(lead, gender);
    const deprThr = depressionThreshold();

    const inNorm = Math.abs(mv) <= 0.1;
    const elevation = mv >= elevThr;
    const depression = mv <= -deprThr;

    if (!inNorm) {
      overallInNorm = false;
    }

    perLead[lead] = {
      uv,
      mv: Number(mv.toFixed(3)),
      inNorm,
      elevation,
      depression,
    };
  });

  // Terytoria: STEMI (≥2 uniesienia)
  const stemiTerritories: Territory[] = (
    Object.keys(TERRITORIES) as Territory[]
  ).filter(t => {
    const leads = TERRITORIES[t];
    const countElev = leads.reduce(
      (acc, L) => acc + (perLead[L].elevation ? 1 : 0),
      0,
    );
    return countElev >= 2;
  });

  // Terytoria: depresje (≥2 obniżenia)
  const depressionTerritories: Territory[] = (
    Object.keys(TERRITORIES) as Territory[]
  ).filter(t => {
    const leads = TERRITORIES[t];
    const countDepr = leads.reduce(
      (acc, L) => acc + (perLead[L].depression ? 1 : 0),
      0,
    );
    return countDepr >= 2;
  });

  return { perLead, overallInNorm, stemiTerritories, depressionTerritories };
}

// ---- Struktury do zwrotu (uproszczony rekord) ----
interface DetailRow {
  lead: LeadName;
  st_mV: number; // 2 miejsca po przecinku
  inNorm: boolean;
}

interface SimpleRecord {
  summary: Summary;
  description: string;
  details: DetailRow[];
}

// ---- Funkcja budująca uproszczony rekord ----
export function buildSimpleRecord(
  rawUv: number[],
  gender: Gender = Gender.Male,
): SimpleRecord {
  const r = interpretST(rawUv, gender);

  // 1) Ogólne podsumowanie
  const hasSTEMI = r.stemiTerritories.length > 0;
  const hasDepression = r.depressionTerritories.length > 0;

  let summary: Summary;
  if (hasSTEMI) {
    summary = 'warn';
  } else if (r.overallInNorm && !hasDepression) {
    summary = 'ok';
  } else {
    summary = 'alert';
  }

  // 2) Krótki opis (katalog tekstów)
  const where = (ts: Territory[]) => {
    const names = ts.map(t => TERR_PL[t]);
    return names.length <= 1
      ? names[0] || ''
      : names.slice(0, -1).join(', ') + ' i ' + names.slice(-1);
  };

  let description: string;
  if (summary === 'ok') {
    description = TEXTS.norm;
  } else if (summary === 'warn') {
    description = TEXTS.stemi(where(r.stemiTerritories));
  } else {
    description = hasDepression
      ? TEXTS.depression(where(r.depressionTerritories))
      : TEXTS.mild;
  }

  // 3) Tabela szczegółów (2 miejsca po przecinku)
  const details: DetailRow[] = LEADS.map(lead => ({
    lead,
    st_mV: Number(r.perLead[lead].mv.toFixed(2)),
    inNorm: r.perLead[lead].inNorm,
  }));

  return { summary, description, details };
}

// ---- Przykład użycia ----
/*
const inputUv = [25, 35, 45, 234, 180, -64]; // µV w kolejności [V1, I, V2, V4, V6, II]
console.log(buildSimpleRecord(inputUv, 'male'));
console.log(buildSimpleRecord(inputUv, 'female'));
*/
